import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CountryCitySelectProps {
  country: string;
  city: string;
  onCountryChange: (country: string) => void;
  onCityChange: (city: string) => void;
  idPrefix?: string;
  countryLabel?: string;
  cityLabel?: string;
  required?: boolean;
}

/**
 * Cascading Country -> City selectors backed by the public `countries` and
 * `cities` tables. Values are stored as plain text (country name, city name)
 * so they remain compatible with existing text columns.
 */
export function CountryCitySelect({
  country,
  city,
  onCountryChange,
  onCityChange,
  idPrefix = "loc",
  countryLabel = "Country",
  cityLabel = "City",
  required = false,
}: CountryCitySelectProps) {
  const { data: countries = [] } = useQuery({
    queryKey: ["countries-select"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const selectedCountryId = countries.find((c) => c.name === country)?.id;

  const { data: cities = [] } = useQuery({
    queryKey: ["cities-by-country", selectedCountryId],
    enabled: !!selectedCountryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cities")
        .select("id, name")
        .eq("country_id", selectedCountryId!)
        .order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-country`}>
          {countryLabel}
          {required ? " *" : ""}
        </Label>
        <Select
          value={country || undefined}
          onValueChange={(v) => {
            onCountryChange(v);
            onCityChange("");
          }}
        >
          <SelectTrigger id={`${idPrefix}-country`}>
            <SelectValue placeholder="Select country..." />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-city`}>{cityLabel}</Label>
        <Select
          value={city || undefined}
          onValueChange={onCityChange}
          disabled={!selectedCountryId}
        >
          <SelectTrigger id={`${idPrefix}-city`}>
            <SelectValue
              placeholder={selectedCountryId ? "Select city..." : "Pick country first"}
            />
          </SelectTrigger>
          <SelectContent>
            {cities.length === 0 && selectedCountryId ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No cities for this country yet
              </div>
            ) : (
              cities.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
